<template>
    <p-form @submit='onSubmit'>
        <div class="container-fluid content-wrapper" v-if="productVariantDetails">
            <b-row class="content-heading">
                <b-col>
                    <div>{{ isNew ? 'Add' : 'Edit' }} Product Variant</div>
                </b-col>
                <b-col cols="auto" class="ms-auto">
                    <p-button variant="primary" type="submit" :is-busy="isBusy">Save</p-button>
                </b-col>
            </b-row>
            <b-row cols="1" cols-md="3">
                <b-col>
                    <p-input
                        label="Variant Name"
                        name="Variant Name"
                        v-model="productVariantDetails.variantName"
                        :rules="variantNameRules"
                        @input="inputDebounce"
                    />
                </b-col>
                <b-col>
                    <p-select
                        dataType="productVariantTypes"
                        label="Category"
                        name="Category"
                        v-model="productVariantDetails.productVariantTypeId"
                        rules="required"
                    />
                </b-col>
                <b-col>
                    <p-select
                        label="Status"
                        v-model="productVariantDetails.status"
                        :options="status"
                        text-field="value"
                    />
                </b-col>
            </b-row>
            <p-card>
                <b-row>
                    <b-col cols="12">
                        <p-table
                            :items="getItems"
                            :enable-row-selection="false"
                            :fields="fields"
                            :pagination="false"
                            :per-page="500"
                            @row-clicked="(item) => item.isActive = !item.isActive"
                            class="fixed-variant-table"
                        >
                            <template v-slot:above-table>
                                <b-row class="mt-3"
                                >
                                    <b-col>
                                        <p-checkbox
                                            v-model="filterSelected"
                                            size="md"
                                            label="Show Only Selected"
                                        ></p-checkbox>
                                    </b-col>
                                </b-row>
                            </template>

                            <template v-slot:cell(isActive)="{ value, item }">
                                <p-checkbox
                                    v-model="item.isActive"
                                    size="md"
                                ></p-checkbox>
                            </template>
                        </p-table>
                    </b-col>
                </b-row>
            </p-card>
            <b-row class="mt-3">
                <b-col class="text-start">
                    <p-button
                        variant="outline-danger"
                        type="button"
                        :is-busy="isBusy"
                        @click="deleteVariant"
                        v-if="!isNew"
                        >Delete</p-button
                    >
                </b-col>
                <b-col class="text-end">
                    <p-button
                        variant="primary"
                        type="submit"
                        :is-busy="isBusy"
                        >Save</p-button
                    >
                </b-col>
            </b-row>
        </div>
    </p-form>
</template>

<script>
import NavigationGuard from '@/components/mixins/NavigationGuard.js';
import editPage from '@/components/mixins/EditPage';
import axios from 'axios';
import debounce from 'lodash/debounce';

export default {
    mixins: [NavigationGuard, editPage],
    components: {},
    data() {
        return {
            isBusy: false,
            nameExists: false,
            filterSelected: false,
            status: [{ value: 'Active' }, { value: 'Inactive' }],
            fields: [
                {
                    key: 'isActive',
                    label: '',
                    thStyle: { width: '60px' },
                    tdStyle: { width: '60px' }
                },
                {
                    key: 'name',
                    label: 'Variant Groups',
                    automaticSearch: true
                }
            ],
            productVariantDetails: null
        };
    },
    watch: {
        productVariantDetails: NavigationGuard.$watcher,
        // Duplicate names are scoped to the variant type, so re-check when the category changes.
        'productVariantDetails.productVariantTypeId'() {
            this.inputDebounce();
        }
    },
    mounted: function() {
        this.inputDebounce = debounce(this.checkNameExists, 300);
        this.loadData();
    },
    computed: {
        variantNameRules() {
            // Object form (built in a computed so the literal isn't recreated every render).
            // isUnique flags a server-detected duplicate; the save endpoint remains the authority.
            return {
                required: true,
                max: { length: 80 },
                isUnique: {
                    isUniqueValue: !this.nameExists,
                    message: 'This product variant name is already in use.'
                }
            };
        },
        getItems() {
            return this.filterSelected
                ? this.productVariantDetails.productVariantGroupings.filter(
                      x => x.isActive == true
                  )
                : this.productVariantDetails.productVariantGroupings;
        },
        isAnyGroupLinked() {
            return (
                this.productVariantDetails.productVariantGroupings.filter(
                    x => x.isActive == true
                ).length > 0
            );
        }
    },
    methods: {
        onSubmit() {
            this.isBusy = true;
            axios
                .post('productvariant', this.productVariantDetails)
                .then(response => {
                    this.dataSaved();
                    if (this.isNew) {
                        this.$router.push(
                            '/productvariant/' + response.data
                        );
                    }
                    this.$appSuccess(
                            `Product variant details saved successfully.`
                        )
                })
                .finally(() => {
                    this.isBusy = false;
                });
        },

        checkNameExists() {
            if (!this.productVariantDetails) {
                return;
            }

            const variantName = this.productVariantDetails.variantName;
            const productVariantTypeId = this.productVariantDetails.productVariantTypeId;

            // Nothing to check until both a name and a category are present. Clear any prior
            // result so the field isn't left flagged as a duplicate.
            if (!variantName || !productVariantTypeId) {
                this.nameExists = false;
                return;
            }

            axios
                .get('productvariant/nameexists', {
                    params: {
                        variantName,
                        productVariantTypeId,
                        // Exclude the record being edited so its own name doesn't read as a duplicate.
                        productVariantId: this.productVariantDetails.productVariantId || null
                    }
                })
                .then(resp => {
                    this.nameExists = resp.data;
                });
        },

        loadData() {
            let promise = this.isNew
                ? axios.get('productvariant/new')
                : axios.get('productvariant', { params: { id: this.id } });

            promise.then(resp => {
                this.productVariantDetails = resp.data;
                this.dataLoaded();
            });
        },

        deleteVariant() {
            if(this.isAnyGroupLinked){
                this.$appError(
                        'Unable to delete variants assigned to groups. Please remove variant from any groups before attempting to delete.'
                    )
            }
            else if (this.productVariantDetails.status == "Active")
            {
                this.$appError(
                        'Unable to delete product variant as it is Active. Please set variant status to inactive before attempting to delete.'
                    )
            }
            else{
                this.isBusy = true;
                axios
                    .post('productvariant/delete', this.productVariantDetails)
                    .then(() => {
                        this.dataSaved();
                        this.$router.push('/productvariants');
                        this.$appSuccess(`Product variant deleted successfully.`)
                    })
                    .finally(() => {
                        this.isBusy = false;
                    });

            }
        }
    }
};
</script>

<style lang="scss">
.fixed-variant-table table {
    table-layout: fixed;
    width: 100%;
}
</style>